import PrivateLayout from "@/components/private/PrivateLayout";
import { ADMIN_NAV } from "@/components/private/adminNav";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";
import { Pencil, Plus, Trash2, UserCog } from "lucide-react";
import PhotoUpload from "@/components/private/PhotoUpload";
import { useState } from "react";
import { toast } from "sonner";

interface CoachForm {
  id?: number;
  name: string;
  title: string;
  bio: string;
  specialty: string;
  photoKey: string;
  displayOrder: number;
  linkedinUrl: string;
  instagramUrl: string;
  isActive: "yes" | "no";
}

const emptyForm: CoachForm = {
  name: "",
  title: "",
  bio: "",
  specialty: "",
  photoKey: "",
  displayOrder: 0,
  linkedinUrl: "",
  instagramUrl: "",
  isActive: "yes",
};

export default function AdminCoaches() {
  const { data, refetch, isLoading } = trpc.coaches.listAll.useQuery();
  const createMut = trpc.coaches.create.useMutation();
  const updateMut = trpc.coaches.update.useMutation();
  const deleteMut = trpc.coaches.delete.useMutation();

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<CoachForm>(emptyForm);

  const save = async () => {
    if (!form.name.trim() || !form.title.trim()) {
      toast.error("Nombre y rol son obligatorios");
      return;
    }
    const { id, ...payload } = form;
    try {
      if (id) {
        await updateMut.mutateAsync({ id, data: payload });
        toast.success("Miembro actualizado");
      } else {
        await createMut.mutateAsync(payload);
        toast.success("Miembro añadido");
      }
      setOpen(false);
      setForm(emptyForm);
      refetch();
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const remove = async (id: number) => {
    if (!confirm("¿Eliminar este miembro del equipo?")) return;
    try {
      await deleteMut.mutateAsync({ id });
      toast.success("Eliminado");
      refetch();
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const openNew = () => {
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (c: any) => {
    setForm({
      id: c.id,
      name: c.name || "",
      title: c.title || "",
      bio: c.bio || "",
      specialty: c.specialty || "",
      photoKey: c.photoKey || "",
      displayOrder: c.displayOrder ?? 0,
      linkedinUrl: c.linkedinUrl || "",
      instagramUrl: c.instagramUrl || "",
      isActive: c.isActive || "yes",
    });
    setOpen(true);
  };

  return (
    <PrivateLayout title="Administración" nav={ADMIN_NAV} requireRole="admin">
      <div className="p-6 md:p-12 max-w-6xl">
        <div className="flex items-end justify-between mb-8 gap-4">
          <div>
            <h1
              className="font-display font-light text-[var(--forest-deep)] leading-[1.05]"
              style={{ fontSize: "clamp(2rem, 4vw, 2.75rem)" }}
            >
              Equipo
            </h1>
            <p className="mt-2 text-sm text-[var(--forest-deep)]/70">
              Biografías y fotos de los profesionales que aparecen en la web pública.
            </p>
          </div>
          <Button
            onClick={openNew}
            className="bg-[var(--forest)] hover:bg-[var(--forest-deep)] text-[var(--cream)] gap-2"
          >
            <Plus size={16} /> Añadir miembro
          </Button>
        </div>

        {isLoading ? (
          <div className="h-96 rounded-xl bg-white/50 animate-pulse" />
        ) : !data || data.length === 0 ? (
          <Card className="p-16 text-center bg-white border border-[var(--forest-deep)]/10">
            <UserCog className="mx-auto text-[var(--forest)]/30 mb-4" size={36} />
            <h2 className="font-display text-2xl font-medium text-[var(--forest-deep)]">
              Sin miembros del equipo aún
            </h2>
            <p className="mt-2 text-[var(--forest-deep)]/70">
              Añade el primer profesional con el botón superior.
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.map((c: any) => (
              <Card
                key={c.id}
                className="p-5 bg-white border border-[var(--forest-deep)]/10"
              >
                <div className="flex gap-4">
                  {c.photoKey ? (
                    <img
                      src={`/manus-storage/${c.photoKey}`}
                      alt={c.name}
                      className="h-16 w-16 rounded-full object-cover"
                    />
                  ) : (
                    <div className="h-16 w-16 rounded-full bg-[var(--forest)]/10 flex items-center justify-center font-display text-xl text-[var(--forest)]">
                      {(c.name || "?").charAt(0)}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[var(--forest-deep)] truncate">
                      {c.name}
                    </div>
                    <div className="text-xs text-[var(--forest-deep)]/60 truncate">
                      {c.title || "Sin rol"}
                    </div>
                    {c.bio && (
                      <p className="text-xs text-[var(--forest-deep)]/70 mt-2 line-clamp-3">
                        {c.bio}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 mt-4 pt-3 border-t border-border/50">
                  <Button variant="outline" size="sm" onClick={() => openEdit(c)}>
                    <Pencil size={13} className="mr-1" /> Editar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => remove(c.id)}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <Trash2 size={13} />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{form.id ? "Editar miembro" : "Nuevo miembro"}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
              <div className="md:col-span-2">
                <Label>Nombre *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div className="md:col-span-2">
                <Label>Rol / titulación *</Label>
                <Input
                  placeholder="Fisioterapeuta deportivo"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                />
              </div>
              <div className="md:col-span-2">
                <Label>Especialidad</Label>
                <Input
                  placeholder="Readaptación deportiva"
                  value={form.specialty}
                  onChange={(e) => setForm({ ...form, specialty: e.target.value })}
                />
              </div>
              <div className="md:col-span-2">
                <Label>Biografía</Label>
                <Textarea
                  rows={4}
                  value={form.bio}
                  onChange={(e) => setForm({ ...form, bio: e.target.value })}
                />
              </div>
              <div className="md:col-span-2">
                <PhotoUpload
                  value={form.photoKey}
                  onChange={(key) => setForm({ ...form, photoKey: key })}
                  folder="coaches"
                  label="Foto del profesional"
                />
              </div>
              <div>
                <Label>LinkedIn</Label>
                <Input
                  value={form.linkedinUrl}
                  onChange={(e) => setForm({ ...form, linkedinUrl: e.target.value })}
                />
              </div>
              <div>
                <Label>Instagram</Label>
                <Input
                  value={form.instagramUrl}
                  onChange={(e) => setForm({ ...form, instagramUrl: e.target.value })}
                />
              </div>
              <div>
                <Label>Orden</Label>
                <Input
                  type="number"
                  value={form.displayOrder}
                  onChange={(e) =>
                    setForm({ ...form, displayOrder: Number(e.target.value) || 0 })
                  }
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>
                Cancelar
              </Button>
              <Button
                onClick={save}
                disabled={createMut.isPending || updateMut.isPending}
                className="bg-[var(--forest)] text-[var(--cream)]"
              >
                Guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </PrivateLayout>
  );
}
